import {isFeatureEnabled} from '@github-ui/feature-flags'
import {testIdProps} from '@github-ui/test-id-props'
import {Button, Stack, useConfirm} from '@primer/react'
import {useCallback} from 'react'

import {ViewOptionsMenuUI} from '../../api/stats/contracts'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {isViewConfigDirty} from '../../hooks/use-view-state-reducer/get-view-dirty-states'
import type {BaseViewState, ViewIsDirtyStates} from '../../hooks/use-view-state-reducer/types'
import {useViews} from '../../hooks/use-views'
import {Resources} from '../../strings'
import styles from './view-changes-button.module.css'

export function ViewChangeButtons({
  view,
  setOpen,
}: {
  view: BaseViewState & ViewIsDirtyStates
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const {hasWritePermissions} = ViewerPrivileges()
  const {saveCurrentViewState, resetViewState} = useViews()
  const displayButtonEnabled = isFeatureEnabled('memex_display_button_config_menu')
  const confirm = useConfirm()

  const handleSaveButtonClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()

      if (displayButtonEnabled) {
        const shouldSave = await confirm({
          title: Resources.confirmSetDefaultChangesTitle(view.name),
          content: Resources.confirmSetDefaultChangesDescription,
          confirmButtonContent: Resources.saveChanges,
          confirmButtonType: 'primary',
          width: 'large',
        })

        if (!shouldSave) {
          return
        }
      }

      // When display button is enabled, only save the config
      // When disabled, save everything (original behavior)
      const saveScope = displayButtonEnabled ? 'config' : undefined
      await saveCurrentViewState(
        view.number,
        {
          ui: ViewOptionsMenuUI,
        },
        saveScope,
      )
      setOpen(false)
    },
    [confirm, displayButtonEnabled, saveCurrentViewState, setOpen, view.name, view.number],
  )

  const handleResetChanges = useCallback(async () => {
    // When display button is enabled, only reset the config
    // When disabled, reset everything (original behavior)
    const resetScope = displayButtonEnabled ? 'config' : undefined
    resetViewState(
      view.number,
      {
        ui: ViewOptionsMenuUI,
      },
      resetScope,
    )

    setOpen(false)
  }, [displayButtonEnabled, resetViewState, setOpen, view.number])

  const configDirty = isViewConfigDirty(view)
  const isConfigDirty = displayButtonEnabled ? configDirty : view.isViewStateDirty

  if (!isConfigDirty) {
    return null
  }

  return (
    <Stack direction="horizontal" justify="end" gap="condensed" padding="normal" className={styles.Footer}>
      <Button onClick={handleResetChanges} {...testIdProps('view-options-menu-reset-changes-button')}>
        {Resources.discardChanges}
      </Button>
      {hasWritePermissions && !view.isDeleted ? (
        <Button
          variant="primary"
          onClick={handleSaveButtonClick}
          disabled={!isConfigDirty}
          {...testIdProps('view-options-menu-save-changes-button')}
        >
          {displayButtonEnabled ? Resources.setDefaultChanges : Resources.saveChanges}
        </Button>
      ) : null}
    </Stack>
  )
}

try{ ViewChangeButtons.displayName ||= 'ViewChangeButtons' } catch {}